import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCurrentUser } from '@/api/auth.ts';
import { readApiError, roverNotificationsApi } from '@/api/Rover/notifications/roverNotifications';
import {
  CHANNEL_LABELS,
  CHANNELS,
  type Channel,
  type ChannelContent,
  type ChannelEnabled,
  type ChannelMessageType,
  MERGE_FIELDS,
  MESSAGE_TYPE_LABELS,
  type MessageOption,
  type MessageType,
  type StatusNotificationConfig,
  type StatusNotificationSummary,
  type UpdateStatusNotificationRequest,
} from '@/api/Rover/notifications/roverNotifications.types';
import { Input } from '@/components/shadcn/input.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import { Textarea } from '@/components/shadcn/textarea.tsx';
import { applicationFormNotificationsClient } from '@/features/ApplicationForm/utils/notifications/applicationFormNotificationsClient';
import { hasCustomContent, inferChannelMessageType, smsSegments } from '@/features/ApplicationForm/utils/notifications/renderMessage';
import {
  type ApplicationFormStatusCatalogItem,
  buildStaticBootstrap,
  isStaticStatusKey,
  staticStatusLabelFromKey,
} from '@/features/ApplicationForm/utils/notifications/staticDefaults';
import { resolveStatusLabelFromApplicationCode } from '@/features/ApplicationForm/utils/notifications/statusCatalog';
import type { CurrentUserInterface } from '@/types/User.ts';
import { sanitizeResponseError } from '@/utils';
import { ChannelPreviewModal } from './components/ChannelPreviewModal';
import { MessageOptionsManager } from './components/MessageOptionsManager';
import { AppButton, ConfirmDialog, CustomSelect, cx, Notice } from './ui/primitives';

/**
 * Notifications screen.
 *
 * The user picks a status and a channel, then sees only that channel's settings.
 * Unsaved edits are kept per status while the user moves between statuses,
 * channels and message types; Save changes saves every edited status.
 */

export type FormNotificationsPageMode = 'rover' | 'applicationForm';

export interface FormNotificationsPageProps {
  formId: string;
  /** Used in previews for {{program_name}}. */
  programName: string;
  /** Used in previews for {{organization_name}}. */
  organizationName?: string;
  /** True when the user may edit Form Settings (wrap the route in RequirePrivilege too). */
  canEdit: boolean;
  /** Status key to open first. Defaults to Submitted when the form has it. */
  initialStatus?: string;
  /** `applicationForm` uses GET/POST /application_forms/:id/notifications. */
  mode?: FormNotificationsPageMode;
  /** Application.status code (e.g. DOCUMENT_REVIEW) to select after staff changes status on application details. */
  highlightApplicationStatusCode?: string | null;
}

type FieldKey = 'email.subject' | 'email.body' | 'sms.body' | 'onscreen.title' | 'onscreen.body';

interface Draft {
  content: ChannelContent;
  enabled: ChannelEnabled;
  messageType: ChannelMessageType;
}

const MESSAGE_TYPES: MessageType[] = ['default', 'custom', 'options'];
const CHANNEL_NOUN: Record<Channel, string> = { email: 'email', sms: 'SMS', onscreen: 'on-screen' };

const getField = (c: ChannelContent, key: FieldKey): string | null => {
  const [ch, f] = key.split('.') as [Channel, string];
  return (c[ch] as Record<string, string | null>)[f];
};
const setField = (c: ChannelContent, key: FieldKey, value: string | null): ChannelContent => {
  const [ch, f] = key.split('.') as [Channel, string];
  return { ...c, [ch]: { ...c[ch], [f]: value } };
};
const toDraft = (c: StatusNotificationConfig): Draft => ({
  content: structuredClone(c.content),
  enabled: { ...c.enabled },
  messageType: { ...c.messageType },
});
const sameDraft = (d: Draft, c: StatusNotificationConfig) =>
  JSON.stringify(d.content) === JSON.stringify(c.content) &&
  JSON.stringify(d.enabled) === JSON.stringify(c.enabled) &&
  JSON.stringify(d.messageType) === JSON.stringify(c.messageType);

const _selectClass =
  'h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:bg-gray-50';
const fieldClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3  text-gray-900 placeholder:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:bg-gray-50';

/** One row per label — avoids duplicate sidebar entries if the API returns repeats. */
const dedupeStatusSummaries = (list: StatusNotificationSummary[]) => {
  const seen = new Set<string>();
  return list.filter((s) => {
    if (seen.has(s.statusLabel)) return false;
    seen.add(s.statusLabel);
    return true;
  });
};

const normalizeConfig = (config: StatusNotificationConfig): StatusNotificationConfig => ({
  ...config,
  messageType: config.messageType ?? inferChannelMessageType(config.content, config.activeOptionCount),
});

const resolveServerStatusId = (
  config: StatusNotificationConfig,
  selected: string | null,
  serverSummaries: StatusNotificationSummary[] | null
): string | null => {
  for (const id of [selected, config.status]) {
    if (id && !isStaticStatusKey(id)) return id;
  }
  return serverSummaries?.find((s) => s.statusLabel === config.statusLabel && !isStaticStatusKey(s.status))?.status ?? null;
};

export function FormNotificationsPage({
  formId,
  programName,
  organizationName = '',
  canEdit,
  initialStatus,
  mode = 'rover',
  highlightApplicationStatusCode = null,
}: FormNotificationsPageProps) {
  const [statusCatalog, setStatusCatalog] = useState<ApplicationFormStatusCatalogItem[]>([]);
  const staticBootstrap = useMemo(
    () => (mode === 'applicationForm' && statusCatalog.length > 0 ? buildStaticBootstrap(statusCatalog) : null),
    [mode, statusCatalog]
  );

  const [statuses, setStatuses] = useState<StatusNotificationSummary[] | null>(null);
  const [serverSummaries, setServerSummaries] = useState<StatusNotificationSummary[] | null>(null);
  const [notificationsSchemaReady, setNotificationsSchemaReady] = useState<boolean | null>(null);
  const [notificationsSchemaError, setNotificationsSchemaError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(initialStatus ?? null);
  const [channel, setChannel] = useState<Channel>('email');
  const [configs, setConfigs] = useState<Record<string, StatusNotificationConfig>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [options, setOptions] = useState<Record<string, MessageOption[]>>({});
  const [savedOptions, setSavedOptions] = useState<Record<string, MessageOption[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const fieldRefs = useRef<Partial<Record<FieldKey, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const lastField = useRef<FieldKey | null>(null);

  const rememberDetail = useCallback((statusKey: string, config: StatusNotificationConfig, opts: MessageOption[]) => {
    const normalized = normalizeConfig(config);
    setConfigs((m) => ({ ...m, [statusKey]: normalized }));
    setDrafts((m) => (m[statusKey] ? m : { ...m, [statusKey]: toDraft(normalized) }));
    setOptions((m) => ({ ...m, [statusKey]: opts }));
    setSavedOptions((m) => ({ ...m, [statusKey]: structuredClone(opts) }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (mode === 'applicationForm') {
          const {
            summaries,
            schemaReady,
            statusCatalog: catalog,
            schemaError,
          } = await applicationFormNotificationsClient.listStatuses(formId);
          if (cancelled) return;
          setStatusCatalog(catalog);
          setNotificationsSchemaReady(schemaReady);
          setNotificationsSchemaError(schemaError ?? null);
          const normalized = dedupeStatusSummaries(summaries);
          const catalogBootstrap = catalog.length > 0 ? buildStaticBootstrap(catalog) : null;
          const list = schemaReady && normalized.length > 0 ? normalized : (catalogBootstrap?.summaries ?? []);
          if (schemaReady && normalized.length > 0) setServerSummaries(normalized);
          else setServerSummaries(null);
          setStatuses(list);
          setStatus((prev) => {
            const base = prev ?? catalogBootstrap?.first ?? list.find((x) => x.status === 'submitted')?.status ?? list[0]?.status ?? null;
            if (!base) return null;
            if (isStaticStatusKey(base)) {
              const label = staticStatusLabelFromKey(base, catalog);
              return list.find((s) => s.statusLabel === label)?.status ?? list[0]?.status ?? base;
            }
            return list.find((s) => s.status === base)?.status ?? list[0]?.status ?? base;
          });
        } else {
          const list = dedupeStatusSummaries(await roverNotificationsApi.listStatuses(formId));
          if (cancelled) return;
          setStatuses(list);
          setStatus((prev) => prev ?? list.find((x) => x.status === 'submitted')?.status ?? list[0]?.status ?? null);
        }
      } catch (e) {
        if (!cancelled && mode !== 'applicationForm') setError(readApiError(e).message);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [formId, mode]);

  useEffect(() => {
    if (!highlightApplicationStatusCode || !statuses?.length) return;
    const label = resolveStatusLabelFromApplicationCode(highlightApplicationStatusCode, statusCatalog);
    const match = statuses.find((s) => s.status === highlightApplicationStatusCode || (label != null && s.statusLabel === label));
    if (match) setStatus(match.status);
  }, [highlightApplicationStatusCode, statusCatalog, statuses]);

  useEffect(() => {
    if (!status || configs[status]) return;
    if (mode === 'applicationForm' && isStaticStatusKey(status)) {
      const detail = staticBootstrap?.load(status);
      if (detail) rememberDetail(status, detail.config, detail.options);
      return;
    }
    let cancelled = false;
    setError(null);
    const loadDetail = async () => {
      try {
        if (mode === 'applicationForm') {
          const { config: c, options: opts } = await applicationFormNotificationsClient.loadStatusDetail(formId, status);
          if (!cancelled) rememberDetail(status, c, opts);
        } else {
          const [c, opts] = await Promise.all([
            roverNotificationsApi.getStatus(formId, status),
            roverNotificationsApi.listOptions(formId, status),
          ]);
          if (!cancelled) rememberDetail(status, c, opts);
        }
      } catch (e) {
        if (!cancelled) setError(readApiError(e).message);
      }
    };
    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [formId, status, mode, configs, staticBootstrap, rememberDetail]);

  const config = status ? configs[status] : undefined;
  const draft = status ? drafts[status] : undefined;
  const statusOptions = (status && options[status]) || [];

  const dirtyStatuses = useMemo(
    () =>
      Object.keys(drafts).filter((s) => {
        const draftChanged = !!configs[s] && !sameDraft(drafts[s], configs[s]);
        const optionsChanged =
          mode === 'applicationForm' && !!savedOptions[s] && JSON.stringify(options[s] ?? []) !== JSON.stringify(savedOptions[s]);
        return draftChanged || optionsChanged;
      }),
    [drafts, configs, mode, options, savedOptions]
  );

  useEffect(() => {
    if (dirtyStatuses.length === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirtyStatuses.length]);

  const updateDraft = useCallback(
    (fn: (d: Draft) => Draft) => {
      if (!status) return;
      setJustSaved(false);
      setDrafts((m) => (m[status] ? { ...m, [status]: fn(m[status]) } : m));
    },
    [status]
  );

  const [currentUser, setCurrentUser] = useState<CurrentUserInterface | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((data) => {
        if (!cancelled) setCurrentUser(data.data);
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, []);

  const setEnabled = (on: boolean) => updateDraft((d) => ({ ...d, enabled: { ...d.enabled, [channel]: on } }));

  /** Copy the default text into the channel's custom fields, unless it already has custom text. */
  const withDefaultCopy = (d: Draft): ChannelContent =>
    !config || hasCustomContent(channel, d.content)
      ? d.content
      : {
          ...d.content,
          [channel]: structuredClone(config.defaults[channel]),
        };

  const setMessageType = (type: MessageType) =>
    updateDraft((d) => ({
      ...d,
      content: type === 'custom' ? withDefaultCopy(d) : d.content,
      messageType: { ...d.messageType, [channel]: type },
    }));

  const customize = () => setMessageType('custom');

  const resetToDefault = () => {
    setConfirmReset(false);
    updateDraft((d) => ({
      ...d,
      content: {
        ...d.content,
        [channel]:
          channel === 'email'
            ? { subject: null, body: null }
            : channel === 'sms'
              ? { body: null }
              : {
                  title: null,
                  body: null,
                },
      },
      messageType: { ...d.messageType, [channel]: 'default' },
    }));
  };

  const updateField = (key: FieldKey, value: string) =>
    updateDraft((d) => ({ ...d, content: setField(d.content, key, value === '' ? null : value) }));

  const insertVariable = (token: string) => {
    if (!draft) return;
    const key: FieldKey = lastField.current && lastField.current.startsWith(channel) ? lastField.current : (`${channel}.body` as FieldKey);
    const el = fieldRefs.current[key];
    const current = getField(draft.content, key) ?? '';
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? start;
    updateField(key, current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const s of dirtyStatuses) {
        const d = drafts[s];
        const current = configs[s];
        if (!d || !current) continue;
        if (mode === 'applicationForm') {
          let schemaReadyForSave = notificationsSchemaReady;
          let schemaErrorForSave = notificationsSchemaError;
          let statusId = resolveServerStatusId(current, s, serverSummaries);
          if (!statusId) {
            const fresh = await applicationFormNotificationsClient.listStatuses(formId);
            schemaReadyForSave = fresh.schemaReady;
            schemaErrorForSave = fresh.schemaError ?? null;
            setNotificationsSchemaReady(fresh.schemaReady);
            setNotificationsSchemaError(schemaErrorForSave);
            if (fresh.schemaReady && fresh.summaries.length > 0) {
              setServerSummaries(fresh.summaries);
              statusId = resolveServerStatusId(current, s, fresh.summaries);
            }
          }
          if (!statusId) {
            setError(
              schemaReadyForSave === false
                ? schemaErrorForSave
                  ? `Notification storage could not be initialized: ${schemaErrorForSave}`
                  : 'Notification storage could not be initialized. Restart the API server and reload this page. If it persists, check backend logs for [notifications].'
                : 'Could not resolve this status on the server. Wait a moment for statuses to load, then try again.'
            );
            setSaving(false);
            return;
          }
          const saved = await applicationFormNotificationsClient.saveStatus(formId, statusId, d, options[s] ?? []);
          const normalized = normalizeConfig(saved.config);
          setConfigs((m) => ({ ...m, [s]: normalized }));
          setDrafts((m) => ({ ...m, [s]: toDraft(normalized) }));
          setOptions((m) => ({ ...m, [s]: saved.options }));
          setSavedOptions((m) => ({ ...m, [s]: structuredClone(saved.options) }));
          setStatuses(
            (list) =>
              list?.map((x) =>
                x.status === s
                  ? {
                      ...x,
                      source: normalized.source,
                      enabled: normalized.enabled,
                      messageType: normalized.messageType,
                    }
                  : x
              ) ?? list
          );
        } else {
          const body: UpdateStatusNotificationRequest = {
            enabled: d.enabled,
            messageType: d.messageType,
            content: d.content,
          };
          const updated = normalizeConfig(await roverNotificationsApi.updateStatus(formId, s, body));
          setConfigs((m) => ({ ...m, [s]: updated }));
          setDrafts((m) => ({ ...m, [s]: toDraft(updated) }));
          setStatuses(
            (list) =>
              list?.map((x) =>
                x.status === s
                  ? {
                      ...x,
                      source: updated.source,
                      enabled: updated.enabled,
                      messageType: updated.messageType,
                    }
                  : x
              ) ?? list
          );
        }
      }
      setJustSaved(true);
    } catch (e) {
      setError(mode === 'applicationForm' ? sanitizeResponseError(e) : readApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const onOptionsChange = (next: MessageOption[]) => {
    if (!status) return;
    setOptions((m) => ({ ...m, [status]: next }));
    if (mode === 'applicationForm') setJustSaved(false);
  };

  const onSettingsChange = async (patch: Pick<UpdateStatusNotificationRequest, 'optionsSelectionMode' | 'optionsFormat'>) => {
    if (!status || !config) return;
    const before = config;
    setConfigs((m) => ({ ...m, [status]: { ...before, ...patch } as StatusNotificationConfig }));
    if (mode === 'applicationForm') return;
    try {
      const updated = normalizeConfig(await roverNotificationsApi.updateStatus(formId, status, patch));
      setConfigs((m) => ({
        ...m,
        [status]: {
          ...m[status],
          optionsSelectionMode: updated.optionsSelectionMode,
          optionsFormat: updated.optionsFormat,
        },
      }));
    } catch (e) {
      setConfigs((m) => ({ ...m, [status]: before }));
      setError(readApiError(e).message);
    }
  };

  const statusLabel = statuses?.find((s) => s.status === status)?.statusLabel ?? config?.statusLabel ?? '';
  const channelLabel = CHANNEL_LABELS[channel];
  const enabled = draft?.enabled[channel] ?? false;
  const messageType: MessageType = draft?.messageType[channel] ?? 'default';
  const enabledList = draft ? CHANNELS.filter((c) => draft.enabled[c]).map((c) => CHANNEL_LABELS[c]) : [];
  const editable = canEdit;

  function _renderMessageWithVariables(text: string) {
    const parts = text.split(/(\{\{[^}]+\}\})/g);

    return parts.map((part, index) =>
      /^\{\{[^}]+\}\}$/.test(part) ? (
        <span key={index} className='font-semibold'>
          {part}
        </span>
      ) : (
        part
      )
    );
  }

  const renderField = (key: FieldKey, label: string, multiline: boolean, rows?: number, hint?: ReactNode) => {
    if (!draft || !config) return null;
    const common = {
      id: `field-${key}`,
      value: getField(draft.content, key) ?? '',
      placeholder: getField(config.defaults, key) ?? '',
      disabled: !editable,
      onFocus: () => {
        lastField.current = key;
      },
    };
    return (
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor={common.id} className='text-sm font-semibold text-gray-800'>
          {label}
        </Label>
        {multiline ? (
          <Textarea
            {...common}
            ref={(el) => {
              fieldRefs.current[key] = el;
            }}
            rows={rows}
            onChange={(e) => updateField(key, e.target.value)}
            className={cx(
              fieldClass,
              `resize-y p-8 leading-relaxed font-normal ${key == 'email.body' ? 'text-lg! min-h-64' : 'text-lg! min-h-20'}  `
            )}
          />
        ) : (
          <Input
            {...common}
            ref={(el) => {
              fieldRefs.current[key] = el;
            }}
            type='text'
            onChange={(e) => updateField(key, e.target.value)}
            className={cx(fieldClass, 'h-11')}
          />
        )}
        {hint}
      </div>
    );
  };

  const smsCount = (text: string) => {
    const s = smsSegments(text);
    return (
      <span className='text-sm text-gray-600'>
        {s.characters} characters · {s.segments} {s.segments === 1 ? 'text' : 'texts'}
        {s.encoding === 'UCS-2' ? ' (special characters shorten each text)' : ''} · variables expand when sent
      </span>
    );
  };

  const channelFields = () => {
    if (!draft) return null;
    if (channel === 'email')
      return (
        <>
          {renderField('email.subject', 'Email subject', false)}
          {renderField('email.body', 'Email body', true, 11)}
        </>
      );
    if (channel === 'sms')
      return renderField('sms.body', 'Text message', true, 4, smsCount(draft.content.sms.body ?? config?.defaults.sms.body ?? ''));
    return (
      <>
        {renderField('onscreen.title', 'Heading', false)}
        {renderField('onscreen.body', 'Message displayed on the applicant’s status page', true, 6)}
      </>
    );
  };

  const defaultPreview = () => {
    if (!config) return null;
    const d = config.defaults;
    const text =
      channel === 'email'
        ? `${d.email.subject ?? ''}\n\n${d.email.body ?? ''}`
        : channel === 'sms'
          ? (d.sms.body ?? '')
          : `${d.onscreen.title ?? ''}\n\n${d.onscreen.body ?? ''}`;
    return (
      <div className='flex flex-col gap-2'>
        <Label className='text-sm font-semibold text-gray-900'>{channelLabel} preview</Label>
        <div className='whitespace-pre-line rounded-xl border border-gray-200 bg-gray-50 px-6 py-5  leading-relaxed text-gray-800'>
          {text}
        </div>
        {channel === 'sms' && smsCount(d.sms.body ?? '')}
        <div className='flex flex-wrap items-center justify-between mt-5 gap-3'>
          <span className='text-sm text-gray-600'>Using the system default message</span>
          {editable && <AppButton onClick={customize}>Customize this message</AppButton>}
        </div>
      </div>
    );
  };

  const customEditor = () => (
    <div className='flex flex-col gap-5'>
      {channelFields()}
      <div className='flex flex-wrap items-center gap-3'>
        {editable && <VariableMenu onInsert={insertVariable} />}
        <AppButton onClick={() => setPreviewOpen(true)}>Preview</AppButton>
        {editable && (
          <AppButton variant='ghost' className='underline bg-transparent' onClick={() => setConfirmReset(true)}>
            Reset to default
          </AppButton>
        )}
      </div>
    </div>
  );

  const optionsEditor = () =>
    config && (
      <div className='flex flex-col gap-5'>
        <p className='text-sm leading-relaxed text-gray-700'>
          When staff change a submission to <strong>{statusLabel}</strong>, they pick from these options. The picks are inserted into the{' '}
          {CHANNEL_NOUN[channel]} message where <code className='font-mono'>{'{{selected_options}}'}</code> appears, or added at the end.
        </p>
        <MessageOptionsManager
          formId={formId}
          config={config}
          options={statusOptions}
          onOptionsChange={onOptionsChange}
          onSettingsChange={onSettingsChange}
          canEdit={editable}
          optionsPersistence={mode === 'applicationForm' ? 'local' : 'api'}
        />
        <details className='group rounded-xl border border-gray-200'>
          <summary className='flex min-h-11 cursor-pointer list-none items-center justify-between px-4 text-sm font-semibold text-gray-800'>
            Edit the {CHANNEL_NOUN[channel]} message the options are inserted into
            <span aria-hidden='true' className='text-gray-500 group-open:rotate-180'>
              ⌄
            </span>
          </summary>
          <div className='flex flex-col gap-5 border-t border-gray-200 p-4'>
            {channelFields()}
            {editable && <VariableMenu onInsert={insertVariable} />}
          </div>
        </details>
        <div>
          <AppButton onClick={() => setPreviewOpen(true)}>Preview with sample options</AppButton>
        </div>
      </div>
    );

  const otherDirty = dirtyStatuses.filter((s) => s !== status).length;

  return (
    <section className='mx-auto flex w-2/3 flex-col gap-6'>
      <header className='flex flex-col gap-1'>
        <h3 className='text-lg text-gray-900'>Notifications</h3>
        <p className='text-sm text-gray-500'>Configure what applicants receive when their submission status changes.</p>
      </header>

      <div className='flex flex-col rounded-xl border border-gray-200 bg-white'>
        <div className='flex flex-col gap-6 px-6 py-7 sm:px-10'>
          {error && <Notice tone='danger'>{error}</Notice>}

          <div className='grid grid-cols-1 gap-5 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]'>
            <div className='flex  flex-col gap-2'>
              <Label className='font-semibold text-gray-900'>Submission status</Label>
              <CustomSelect
                value={status ?? ''}
                disabled={!statuses}
                className='py-5 w-full focus-visible:ring-1 focus-visible:ring-green-400 rounded-lg'
                options={
                  statuses?.map((s) => ({
                    value: s.status,
                    label: `${s.statusLabel}${dirtyStatuses.includes(s.status) ? ' (unsaved)' : ''}`,
                  })) ?? []
                }
                onValueChange={(value) => setStatus(value)}
              ></CustomSelect>

              {/*<select*/}
              {/*  className={selectClass}*/}
              {/*  value={status ?? ''}*/}
              {/*  disabled={!statuses}*/}
              {/*  onChange={(e) => setStatus(e.target.value)}*/}
              {/*>*/}
              {/*  {statuses?.map((s) => (*/}
              {/*    <option key={s.status} value={s.status} className="hover:text-green-600">*/}
              {/*      {s.statusLabel}*/}
              {/*      {dirtyStatuses.includes(s.status) ? ' (unsaved)' : ''}*/}
              {/*    </option>*/}
              {/*  ))}*/}
              {/*</select>*/}
            </div>
            <div className='flex flex-col gap-2'>
              <Label className='font-semibold text-gray-900'>Channel</Label>

              <CustomSelect
                value={channel}
                className='py-5 w-full focus-visible:ring-1 focus-visible:ring-green-400 rounded-lg'
                options={CHANNELS.map((c) => ({
                  value: c,
                  label: CHANNEL_LABELS[c],
                }))}
                onValueChange={(value) => setChannel(value as Channel)}
              />
            </div>
          </div>

          {!draft || !config ? (
            <p className='text-sm text-gray-600'>Loading…</p>
          ) : (
            <>
              <div className='flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-6'>
                <Label className='flex min-h-11 cursor-pointer items-center gap-3  text-gray-900'>
                  <input
                    type='checkbox'
                    checked={enabled}
                    disabled={!editable}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className='h-5 w-5 accent-emerald-700'
                  />
                  Enable {CHANNEL_NOUN[channel]} notification
                </Label>
                <span className='text-sm text-gray-600'>
                  {enabledList.length ? `Enabled: ${enabledList.join(', ')}` : `No channels enabled for ${statusLabel}`}
                </span>
              </div>

              {!enabled ? (
                <p className='rounded-xl bg-gray-50 px-5 py-4 text-gray-700'>
                  {channelLabel} notifications are disabled for {statusLabel}.
                </p>
              ) : (
                <>
                  <div className='flex max-w-md flex-col gap-2'>
                    <Label className='font-semibold text-gray-900'>Message type</Label>
                    {/*<select*/}
                    {/*  id='notif-type'*/}
                    {/*  className={`${selectClass} pr-5`}*/}
                    {/*  value={messageType}*/}
                    {/*  disabled={!editable}*/}
                    {/*  onChange={(e) => setMessageType(e.target.value as MessageType)}*/}
                    {/*>*/}
                    {/*  {MESSAGE_TYPES.map((t) => (*/}
                    {/*    <option key={t} value={t}>*/}
                    {/*      {MESSAGE_TYPE_LABELS[t]}*/}
                    {/*    </option>*/}
                    {/*  ))}*/}
                    {/*</select>*/}

                    <CustomSelect
                      value={messageType ?? ''}
                      disabled={!editable}
                      className='py-5 w-full focus-visible:ring-1 focus-visible:ring-green-400'
                      options={MESSAGE_TYPES.map((c) => ({
                        value: c,
                        label: MESSAGE_TYPE_LABELS[c],
                      }))}
                      onValueChange={(value) => setMessageType(value as MessageType)}
                    />
                  </div>
                  {messageType === 'default' && defaultPreview()}
                  {messageType === 'custom' && customEditor()}
                  {messageType === 'options' && optionsEditor()}
                </>
              )}
            </>
          )}
        </div>

        <footer className='flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-5 sm:px-10'>
          <span className='text-sm text-gray-600' aria-live='polite'>
            {statusLabel} · {channelLabel} · {enabled ? MESSAGE_TYPE_LABELS[messageType] : 'Off'}
            {dirtyStatuses.length > 0 && ` · Unsaved changes${otherDirty > 0 ? ` (${dirtyStatuses.length} statuses)` : ''}`}
            {justSaved && dirtyStatuses.length === 0 && ' · Saved'}
          </span>
          {editable && (
            <AppButton variant='primary' onClick={save} disabled={dirtyStatuses.length === 0 || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </AppButton>
          )}
        </footer>
      </div>

      {config && draft && (
        <ChannelPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          formId={formId}
          programName={programName}
          organizationName={organizationName}
          status={config.status}
          statusLabel={statusLabel}
          channel={channel}
          messageType={messageType}
          content={draft.content}
          defaults={config.defaults}
          options={statusOptions}
          optionsFormat={config.optionsFormat}
          enableTestSend={mode !== 'applicationForm'}
          titleFrom={currentUser!.name}
          titleEmail={currentUser!.email}
        />
      )}
      <ConfirmDialog
        open={confirmReset}
        title='Reset to the default message?'
        message={`Your custom ${CHANNEL_NOUN[channel]} text for ${statusLabel} will be removed when you save.`}
        confirmLabel='Reset to default'
        tone='danger'
        onCancel={() => setConfirmReset(false)}
        onConfirm={resetToDefault}
      />
    </section>
  );
}

/** "Insert variable" menu: inserts a merge field at the cursor of the last field the user was in. */
function VariableMenu({ onInsert }: { onInsert: (token: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className='relative'>
      <AppButton aria-haspopup='true' aria-expanded={open} onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)}>
        Insert variable
      </AppButton>
      {open && (
        <ul className='absolute left-0 z-20 mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg'>
          {MERGE_FIELDS.map((f) => (
            <li key={f}>
              <AppButton
                type='button'
                variant={'ghost'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onInsert(`{{${f}}}`);
                  setOpen(false);
                }}
                className='block min-h-10 w-full rounded-md bg-transparent px-4 text-left font-mono text-sm font-normal text-gray-800 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none'
              >
                {`{{${f}}}`}
              </AppButton>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
